import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { ApiError, parseApiError } from '@ooh/contracts';
import { toast } from 'sonner';
import { Sentry } from './lib/sentry';
import { apiErrorToUserMessage } from './lib/api-error-messages';

/**
 * HTTP statuses where a definitive server-side state (not found, gone,
 * forbidden, conflicted, unauthenticated) means retrying can never succeed —
 * only delays the correct error/redirect UI behind several seconds of
 * exponential-backoff retries. Kept as a lookup set (not sequential `if`s) so
 * adding a status here never grows `retry`'s cyclomatic complexity.
 */
const NON_RETRYABLE_STATUSES = new Set([401, 403, 404, 409, 410]);

/**
 * Decides whether a query/mutation failure represents an actual bug worth
 * reporting to Sentry, versus an expected app state already handled by UX
 * (toasts, redirects, permission gates). Non-ApiErrors (network failures,
 * Zod parse mismatches, unexpected exceptions) and 5xx ApiErrors are real
 * server/client bugs. Any 4xx ApiError is an intentional server response
 * already classified and handled elsewhere — never reported.
 */
function shouldReportToSentry(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status >= 500;
}

/**
 * Factory for the shared QueryClient.
 * Extracted so it can be unit-tested independently of the React tree.
 */
export function createQueryClient(): QueryClient {
  const queryCache = new QueryCache({
    onError: (error, query) => {
      // A caller that already renders its own inline failure state (e.g. an
      // action-breakdown preview hook's "Nie udało się obliczyć skutków..." message
      // + retry) sets this flag so the failure is never ALSO presented as an
      // unscoped global "server error" toast alongside its own scoped message
      // (action-breakdown / "Breakdown loading, failure, and empty states" —
      // "SHALL NOT present the failure as though the action itself had failed").
      // Mirrors how a mutation with its own `onError` already fully bypasses this
      // same global handler (see MutationCache.onError below).
      if (query.meta?.['suppressGlobalErrorToast'] === true) return;
      // Skip 401 - handled by auth interceptor
      if (error instanceof ApiError && error.status === 401) return;
      // 403 Forbidden — permission denied for specific resource/action, not a system error.
      // Surfacing this as a global toast would confuse users who legitimately lack permissions
      // on some endpoints (e.g. non-admin accessing admin routes). Handled at component level.
      if (error instanceof ApiError && error.status === 403) return;
      // Emit console.error only for non-ApiError failures (Zod parse errors, network errors,
      // unexpected exceptions). ApiErrors represent HTTP responses the server intentionally
      // sent — e2e tests explicitly verify those error states (404 not-found UI, 500 error
      // banners, etc.) and must not be broken by this hook. The Playwright `noErrors`
      // auto-fixture in `e2e/base.ts` listens to console.error to catch silent failures like
      // Zod schema mismatches on 2xx responses that would otherwise cause 30-second timeouts.
      if (!(error instanceof ApiError)) {
        console.error(`[QueryClient] query failed — key: ${JSON.stringify(query.queryKey)}`, error);
      }
      if (shouldReportToSentry(error)) {
        Sentry.captureException(error, {
          tags: { source: 'query-cache' },
          extra: { queryKey: query.queryKey },
        });
      }
      const parsed = parseApiError(error);
      toast.error(apiErrorToUserMessage(parsed, 'query'));
    },
  });

  const mutationCache = new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip if mutation has its own onError handler
      if (mutation.options.onError) return;
      // OfferConflictError (409) is always handled at the call site via per-call onError.
      // The per-call handler is not reflected in mutation.options.onError, so we guard here
      // to prevent the generic toast from firing alongside the specific conflict message.
      if (error instanceof Error && error.name === 'OfferConflictError') return;
      if (shouldReportToSentry(error)) {
        Sentry.captureException(error, { tags: { source: 'mutation-cache' } });
      }
      const parsed = parseApiError(error);
      toast.error(apiErrorToUserMessage(parsed, 'mutation'));
    },
  });

  return new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // 401 (auth interceptor handles token refresh), 403 (won't change without
          // re-auth), 404/410 (resource definitively not-found/gone), 409 (conflict won't
          // resolve itself) — see NON_RETRYABLE_STATUSES doc comment above.
          if (error instanceof ApiError && NON_RETRYABLE_STATUSES.has(error.status)) return false;
          // Default: retry up to 3 times for other errors
          return failureCount < 3;
        },
      },
    },
  });
}
